import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Gamepad2, Loader2, Pencil, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { eaApi } from '../lib/api';

const Stat = ({ label, value, color = 'text-white' }) => (
  <div className="bg-[#050508] border border-white/10 rounded p-3 text-center">
    <p className={`font-mono font-bold text-lg ${color}`}>{value}</p>
    <p className="text-[9px] uppercase tracking-widest text-slate-500 mt-1">{label}</p>
  </div>
);

export const EAPlayerStatsCard = ({ userId, editable = false, currentEaGamertag, isItalian }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [tag, setTag] = useState(currentEaGamertag || '');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await eaApi.playerStats(userId);
      setData(res.data);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const saveTag = async () => {
    if (!tag.trim()) return;
    setSaving(true);
    try {
      await eaApi.setGamertag(tag.trim());
      toast.success(isItalian ? 'Gamertag EA salvato' : 'EA gamertag saved');
      setEditing(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const s = data?.stats;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="bg-[#0a0d15] border border-white/10"
      data-testid="ea-player-stats-card"
    >
      <div className="px-6 py-4 border-b border-white/10 flex items-center gap-2">
        <Gamepad2 size={18} className="text-[#00F0FF]" />
        <h3 className="font-heading uppercase tracking-widest text-sm font-bold">
          {isItalian ? 'Statistiche EA' : 'EA Stats'}
        </h3>
        {data?.linked && (
          <span className="px-2 py-0.5 text-[10px] rounded bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 uppercase tracking-wider">
            {data.ea_link?.ea_club_name}
          </span>
        )}
      </div>
      <div className="p-6 space-y-4">
        {editable && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold shrink-0">Gamertag EA:</span>
            {editing ? (
              <>
                <Input value={tag} onChange={(e) => setTag(e.target.value)} className="h-8 bg-[#050508] border-white/10 text-sm" data-testid="ea-gamertag-input" />
                <Button size="sm" className="h-8 bg-[#00E676]/15 border border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/25" disabled={saving} onClick={saveTag} data-testid="ea-gamertag-save-btn">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                </Button>
                <Button size="sm" variant="ghost" className="h-8 text-slate-400" onClick={() => setEditing(false)}>
                  <X size={13} />
                </Button>
              </>
            ) : (
              <>
                <span className="text-sm text-white font-mono" data-testid="ea-gamertag-value">
                  {data?.lookup_gamertag || currentEaGamertag || '—'}
                </span>
                <Button size="sm" variant="ghost" className="h-7 text-slate-400 hover:text-white" onClick={() => { setTag(data?.lookup_gamertag || currentEaGamertag || ''); setEditing(true); }} data-testid="ea-gamertag-edit-btn">
                  <Pencil size={12} />
                </Button>
              </>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-3">
            <Loader2 size={15} className="animate-spin" />
            {isItalian ? 'Caricamento da EA…' : 'Loading from EA…'}
          </div>
        ) : !data?.linked ? (
          <p className="text-sm text-slate-500" data-testid="ea-player-not-linked">
            {data?.reason === 'no_club'
              ? (isItalian ? 'Devi far parte di un club per vedere le statistiche EA.' : 'Join a club to see EA stats.')
              : (isItalian ? 'Il tuo club non è ancora collegato a EA Pro Clubs (lo fa il capitano dalla pagina Club).' : 'Your club is not linked to EA Pro Clubs yet (the captain can link it from the Club page).')}
          </p>
        ) : !data?.found ? (
          <p className="text-sm text-slate-500" data-testid="ea-player-not-found">
            {isItalian
              ? `Nessun giocatore "${data?.lookup_gamertag}" trovato nel club EA. Imposta il tuo Gamertag EA esatto qui sopra.`
              : `No player "${data?.lookup_gamertag}" found in the EA club. Set your exact EA gamertag above.`}
          </p>
        ) : (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="ea-player-stats">
            <Stat label={isItalian ? 'Partite' : 'Games'} value={s.games_played} />
            <Stat label={isItalian ? 'Gol' : 'Goals'} value={s.goals} color="text-[#00E676]" />
            <Stat label="Assist" value={s.assists} color="text-[#D500F9]" />
            <Stat label={isItalian ? 'Media Voto' : 'Avg Rating'} value={s.rating_avg?.toFixed(1)} color="text-[#FFEA00]" />
            <Stat label="Win %" value={`${s.win_rate}%`} color="text-[#00F0FF]" />
            <Stat label={isItalian ? 'Ruolo' : 'Position'} value={s.favorite_position || '–'} color="text-[#FF9100]" />
          </div>
        )}
      </div>
    </motion.div>
  );
};
